import { useCallback, useEffect, useRef, useState } from "react";
import {
  APP_BUILD_AT,
  APP_BUILD_VERSION,
} from "../generatedBuildVersion";

const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const VERSION_CHECK_DEDUPE_MS = 10 * 1000;

const getLatestVersion = async () => {
  const response = await fetch(`/version.json?t=${Date.now()}`, {
    cache: "no-store",
    headers: {
      Accept: "application/json",
      "Cache-Control": "no-cache",
    },
  });

  if (!response.ok) {
    throw new Error(`Version check failed (${response.status})`);
  }

  const payload = await response.json();
  if (!payload?.version) throw new Error("Invalid version response");
  return payload;
};

const clearBrowserCaches = async () => {
  if ("serviceWorker" in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registrations.map((registration) => registration.unregister()),
    );
  }

  if ("caches" in window) {
    const cacheKeys = await caches.keys();
    await Promise.all(cacheKeys.map((key) => caches.delete(key)));
  }
};

export default function AppVersionGuard({ children }) {
  const [latestBuild, setLatestBuild] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState("");
  const lastCheckedAtRef = useRef(0);

  const checkVersion = useCallback(async ({ force = false } = {}) => {
    const now = Date.now();
    if (!force && now - lastCheckedAtRef.current < VERSION_CHECK_DEDUPE_MS) {
      return;
    }
    lastCheckedAtRef.current = now;

    try {
      const serverBuild = await getLatestVersion();
      if (serverBuild.version !== APP_BUILD_VERSION) {
        setLatestBuild(serverBuild);
      } else {
        setLatestBuild(null);
      }
    } catch (error) {
      // A temporary offline state should not block the app. The next focus,
      // resume, online event, or interval will retry automatically.
      console.debug("App version check skipped:", error?.message);
    }
  }, []);

  useEffect(() => {
    const initialCheckId = window.setTimeout(
      () => checkVersion({ force: true }),
      800,
    );
    const intervalId = window.setInterval(
      () => checkVersion({ force: true }),
      VERSION_CHECK_INTERVAL_MS,
    );

    const handleVisible = () => {
      if (document.visibilityState === "visible") checkVersion();
    };
    const handleResume = () => checkVersion({ force: true });
    const handleOnline = () => checkVersion({ force: true });

    document.addEventListener("visibilitychange", handleVisible);
    window.addEventListener("focus", handleVisible);
    window.addEventListener("cinema:resume", handleResume);
    window.addEventListener("online", handleOnline);

    return () => {
      window.clearTimeout(initialCheckId);
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisible);
      window.removeEventListener("focus", handleVisible);
      window.removeEventListener("cinema:resume", handleResume);
      window.removeEventListener("online", handleOnline);
    };
  }, [checkVersion]);

  const updateToLatest = async () => {
    if (updating) return;
    setUpdating(true);
    setUpdateError("");

    try {
      const serverBuild = await getLatestVersion();
      await clearBrowserCaches();

      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set("app_update", serverBuild.version);
      window.location.replace(nextUrl.toString());
    } catch (error) {
      setUpdateError("อัปเดตไม่สำเร็จ กรุณาตรวจสอบอินเทอร์เน็ตแล้วลองอีกครั้ง");
      setUpdating(false);
    }
  };

  return (
    <>
      {children}

      {latestBuild ? (
        <div className="app-version-update-backdrop" role="presentation">
          <section
            className="app-version-update-card"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="app-version-update-title"
          >
            <div className="app-version-update-icon" aria-hidden="true">
              ↻
            </div>
            <h2 id="app-version-update-title">มีเวอร์ชันใหม่</h2>
            <p>
              เวอร์ชันที่กำลังใช้อยู่ไม่ใช่เวอร์ชันล่าสุด
              กรุณาอัปเดตก่อนใช้งานต่อ
            </p>
            <div className="app-version-update-meta">
              เวอร์ชันปัจจุบัน: {APP_BUILD_VERSION}
              <br />
              เวอร์ชันล่าสุด: {latestBuild.version}
            </div>
            {updateError ? (
              <div className="app-version-update-error">{updateError}</div>
            ) : null}
            <button
              type="button"
              className="app-version-update-button"
              disabled={updating}
              onClick={updateToLatest}
            >
              {updating ? "กำลังอัปเดต..." : "อัปเดตเป็นเวอร์ชันล่าสุด"}
            </button>
          </section>
        </div>
      ) : null}

      <span hidden data-app-build={APP_BUILD_VERSION} data-app-built-at={APP_BUILD_AT} />

      <style>{`
        .app-version-update-backdrop {
          position: fixed;
          inset: 0;
          z-index: 2147483647;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: calc(24px + env(safe-area-inset-top, 0px)) 20px
            calc(24px + env(safe-area-inset-bottom, 0px));
          background: rgba(0, 0, 0, 0.76);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }

        .app-version-update-card {
          width: min(100%, 420px);
          box-sizing: border-box;
          padding: 28px 22px 22px;
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 24px;
          background: linear-gradient(145deg, #25130f, #151515 72%);
          box-shadow: 0 24px 80px rgba(0, 0, 0, 0.55);
          color: #fff;
          text-align: center;
        }

        .app-version-update-icon {
          display: inline-flex;
          width: 56px;
          height: 56px;
          align-items: center;
          justify-content: center;
          margin-bottom: 12px;
          border-radius: 18px;
          background: linear-gradient(135deg, #e5312a, #f8cc46);
          font-size: 34px;
          font-weight: 900;
          line-height: 1;
        }

        .app-version-update-card h2 {
          margin: 0 0 10px;
          color: #fff;
          font-size: 24px;
        }

        .app-version-update-card p {
          margin: 0;
          color: rgba(255, 255, 255, 0.82);
          font-size: 15px;
          line-height: 1.6;
        }

        .app-version-update-meta {
          margin: 16px 0;
          color: rgba(255, 255, 255, 0.48);
          font-size: 10px;
          line-height: 1.6;
          overflow-wrap: anywhere;
        }

        .app-version-update-error {
          margin: 0 0 12px;
          color: #ff9f9b;
          font-size: 13px;
        }

        .app-version-update-button {
          width: 100%;
          min-height: 50px;
          border: 0;
          border-radius: 15px;
          background: linear-gradient(135deg, #e5312a, #f8a928);
          color: #fff;
          font-size: 15px;
          font-weight: 900;
          cursor: pointer;
          touch-action: manipulation;
          -webkit-tap-highlight-color: transparent;
        }

        .app-version-update-button:disabled {
          cursor: wait;
          opacity: 0.65;
        }
      `}</style>
    </>
  );
}
